import { useState } from "react";
import { Check, Plug, X } from "lucide-react";

import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";
import { SCOPE_LABELS, TIER_NOTES } from "../lib/scopes.js";
import { Spinner, ago, useToast } from "./ui.jsx";

/**
 * Apps connected over MCP, and the changes they are waiting to make.
 *
 * Only a deletion waits here (server/src/lib/mcp-tools.js, asksFirst): the app's
 * tool call is holding for a minute while this is answered, so it leads the
 * page. Approving asks for the passkey, for that one deletion.
 */
export function ConnectedApps() {
    const [grants, setGrants] = useState(null);
    const [approvals, setApprovals] = useState([]);
    const toast = useToast();

    useLive(["oauth"], () => {
        api.oauth.grants().then(setGrants).catch(() => setGrants([]));
        api.oauth.approvals().then(setApprovals).catch(() => setApprovals([]));
    });

    const answer = async (approval, approve) => {
        try {
            await (approve ? api.oauth.approveChange(approval.id) : api.oauth.denyChange(approval.id));
            toast(approve ? `Approved: ${approval.summary}` : "Declined");
            setApprovals((previous) => previous.filter((a) => a.id !== approval.id));
        } catch (error) {
            toast(error.message, "error");
        }
    };

    const disconnect = async (grant) => {
        if (!window.confirm(`Disconnect ${grant.clientName}? It loses access at once and has to be connected again.`)) return;
        try {
            await api.oauth.disconnect(grant.id);
            toast(`Disconnected ${grant.clientName}`);
            setGrants((previous) => previous.filter((g) => g.id !== grant.id));
        } catch (error) {
            toast(error.message, "error");
        }
    };

    if (!grants) return <Spinner className="muted" />;

    return (
        <div className="space-y-6">
            {approvals.length ? (
                <section className="space-y-2">
                    <h2 className="section-title">Waiting for you</h2>
                    <div className="card divide-y divide-gray-100 dark:divide-gray-800">
                        {approvals.map((approval) => (
                            <div key={approval.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5 text-sm">
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium break-words">{approval.summary}</p>
                                    <p className="muted text-xs">
                                        {approval.clientName} · asked {ago(approval.createdAt)}
                                        {approval.tier && approval.tier !== "write" ? ` · ${TIER_NOTES[approval.tier] ?? ""}` : ""}
                                    </p>
                                    {approval.detail ? (
                                        <pre className="mt-2 max-h-80 overflow-auto rounded bg-gray-50 p-2 font-mono text-xs whitespace-pre-wrap break-words dark:bg-gray-900">
                                            {approval.detail}
                                        </pre>
                                    ) : null}
                                </div>
                                <div className="flex shrink-0 gap-1">
                                    <button className="btn-ghost px-2 py-1" onClick={() => answer(approval, false)}><X size={12} /> Decline</button>
                                    <button className="btn-primary px-2 py-1" onClick={() => answer(approval, true)}><Check size={12} /> Approve</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            ) : null}

            <section className="space-y-2">
                <h2 className="section-title">Connected apps</h2>
                {grants.length ? (
                    <div className="card divide-y divide-gray-100 dark:divide-gray-800">
                        {grants.map((grant) => (
                            <div key={grant.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5 text-sm">
                                <div className="min-w-0">
                                    <p className="flex flex-wrap items-center gap-2">
                                        {grant.clientName}
                                        <code className="muted font-mono text-xs">{grant.redirect}</code>
                                    </p>
                                    <p className="muted text-xs" title={grant.scopes.map((s) => SCOPE_LABELS[s] ?? s).join("\n")}>
                                        {grant.scopes.join(", ")} · {grant.lastUsedAt ? `used ${ago(grant.lastUsedAt)}` : `connected ${ago(grant.createdAt)}`}
                                    </p>
                                    {grant.outdated ? (
                                        <p className="text-xs text-amber-700 dark:text-amber-400">
                                            Connected before the nginx, certificate and session tools. Disconnect and connect it again to offer them.
                                        </p>
                                    ) : null}
                                </div>
                                <button className="btn-ghost shrink-0 px-2 py-1" onClick={() => disconnect(grant)}>Disconnect</button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="card flex items-start gap-2 p-4 text-sm muted">
                        <Plug size={14} className="mt-0.5 shrink-0" />
                        <span>
                            No app is connected. In Grok, Claude or Cursor, add a custom MCP connector with the URL{" "}
                            <code className="font-mono">{window.location.origin}/mcp</code>; it will send you here to approve it.
                        </span>
                    </p>
                )}
                <p className="text-xs muted">
                    A connected app reads and changes what its permissions allow, and every change is in the audit log.
                    Only a deletion waits here for your passkey.
                </p>
            </section>
        </div>
    );
}
